import { APP_TAB } from './app';
import { CALL_TARGET } from './call';
import { FLUID_QUERY, READING_LOCATION } from '@/features/fluid/constants/reading';

export const ASSISTANT_REPLY_DELAY_MS = 850;

// Replies in match order; each id owns the message `nkf.assistant.replies.<id>`.
export const ASSISTANT_REPLY = Object.freeze({
  EMERGENCY: 'emergency',
  HELLO: 'hello',
  THANKS: 'thanks',
  IDWG: 'idwg',
  DRY_WEIGHT: 'dryWeight',
  POTASSIUM: 'potassium',
  FLUID: 'fluid',
  MEAL: 'meal',
  BLOOD_PRESSURE: 'bloodPressure',
  BLOOD_TEST: 'bloodTest',
  MEDICATION: 'medication',
  GOALS: 'goals',
  SYMPTOM: 'symptom',
  SCHEDULE: 'schedule',
  LANGUAGE: 'language',
  LOGIN: 'login',
  CONTACT: 'contact',
  APPROXIMATE: 'approximate',
  WEEKLY: 'weekly',
  FALLBACK: 'fallback',
});

// A reply button either opens a route (`to`) or the shared call sheet (`call`).
export const ASSISTANT_ACTION = Object.freeze({
  CALL_EMERGENCY: {
    labelKey: 'nkf.assistant.actions.call995',
    call: CALL_TARGET.EMERGENCY,
    variant: 'destructive-soft',
  },
  CALL_CARE_TEAM: {
    labelKey: 'nkf.assistant.actions.callCareTeam',
    call: CALL_TARGET.CARE_TEAM,
    variant: 'success',
  },
  CALL_SUPPORT: {
    labelKey: 'nkf.assistant.actions.callSupport',
    call: CALL_TARGET.LOGIN_SUPPORT,
    variant: 'info',
  },
  OPEN_FLUID: { labelKey: 'nkf.assistant.actions.openFluid', to: '/fluid', variant: 'info' },
  VIEW_DRY_WEIGHT: {
    labelKey: 'nkf.assistant.actions.viewDryWeight',
    to: { path: '/fluid', query: { [FLUID_QUERY.DRY_WEIGHT_REVIEW]: 'true' } },
    variant: 'info',
  },
  OPEN_FOOD_GUIDE: {
    labelKey: 'nkf.assistant.actions.openFoodGuide',
    to: '/diet/guide',
    variant: 'info',
  },
  BLOOD_RESULTS: {
    labelKey: 'nkf.assistant.actions.bloodResults',
    to: '/blood-results',
    variant: 'info',
  },
  OPEN_RESULTS: {
    labelKey: 'nkf.assistant.actions.openResults',
    to: '/blood-results',
    variant: 'info',
  },
  LOG_WEIGHT_BP: {
    labelKey: 'nkf.assistant.actions.logWeightBp',
    to: { path: '/fluid', query: { [FLUID_QUERY.ENTRY]: READING_LOCATION.HOME } },
    variant: 'info',
  },
  OPEN_EDUCATION: {
    labelKey: 'nkf.education',
    to: '/education',
    variant: 'info',
  },
  LOG_MEAL: {
    labelKey: 'nkf.assistant.actions.logMeal',
    to: '/diet/diary?add=true',
    variant: 'info',
  },
  OPEN_DIARY: {
    labelKey: 'nkf.assistant.actions.openDiary',
    to: '/diet/diary',
    variant: 'info',
  },
  MEDICATION_REMINDERS: {
    labelKey: 'nkf.assistant.actions.medicationReminders',
    to: '/medications',
    variant: 'info',
  },
  GOALS: { labelKey: 'nkf.assistant.actions.goals', to: '/goals', variant: 'info' },
  OPEN_SYMPTOMS: {
    labelKey: 'nkf.assistant.actions.openSymptoms',
    to: '/symptoms',
    variant: 'info',
  },
  OPEN_PROFILE: {
    labelKey: 'nkf.assistant.actions.openProfile',
    to: '/profile',
    variant: 'info',
  },
  SWITCH_LANGUAGE: {
    labelKey: 'nkf.assistant.actions.switchLanguage',
    to: '/profile',
    variant: 'info',
  },
  SEE_CONTACTS: {
    labelKey: 'nkf.assistant.actions.seeContacts',
    to: '/support/care-team',
    variant: 'info',
  },
  OPEN_FAQ: { labelKey: 'nkf.assistant.actions.openFaq', to: '/faq', variant: 'info' },
  WEEKLY_SUMMARY: {
    labelKey: 'nkf.assistant.actions.weeklySummary',
    to: '/diet/weekly',
    variant: 'info',
  },
});

// Free text is lower-cased and matched by substring; the first matching rule answers.
export const ASSISTANT_REPLY_RULES = Object.freeze([
  {
    reply: ASSISTANT_REPLY.EMERGENCY,
    keywords: [
      "can't breathe",
      'can’t breathe',
      'cant breathe',
      'chest pain',
      'faint',
      'unconscious',
      'emergency',
      'severe pain',
      'difficulty breathing',
      'short of breath',
    ],
    actions: [ASSISTANT_ACTION.CALL_EMERGENCY, ASSISTANT_ACTION.CALL_CARE_TEAM],
  },
  {
    reply: ASSISTANT_REPLY.HELLO,
    keywords: [' hello ', ' hi ', ' hey ', 'xin chào', 'good morning'],
    actions: [],
  },
  { reply: ASSISTANT_REPLY.THANKS, keywords: ['thank'], actions: [] },
  {
    reply: ASSISTANT_REPLY.IDWG,
    keywords: ['idwg', 'interdialytic'],
    actions: [ASSISTANT_ACTION.OPEN_FLUID],
  },
  {
    reply: ASSISTANT_REPLY.DRY_WEIGHT,
    keywords: ['dry weight'],
    actions: [ASSISTANT_ACTION.VIEW_DRY_WEIGHT],
  },
  {
    reply: ASSISTANT_REPLY.POTASSIUM,
    keywords: ['potassium', 'phosph', 'banana'],
    actions: [ASSISTANT_ACTION.OPEN_FOOD_GUIDE, ASSISTANT_ACTION.BLOOD_RESULTS],
  },
  {
    reply: ASSISTANT_REPLY.FLUID,
    keywords: ['weight', 'fluid', 'thirst', 'drink', 'water', 'salt'],
    actions: [ASSISTANT_ACTION.LOG_WEIGHT_BP, ASSISTANT_ACTION.OPEN_EDUCATION],
  },
  {
    reply: ASSISTANT_REPLY.MEAL,
    keywords: ['log a meal', 'meal', 'photo', 'eat ', 'food', 'diet'],
    actions: [ASSISTANT_ACTION.LOG_MEAL, ASSISTANT_ACTION.OPEN_DIARY],
  },
  {
    reply: ASSISTANT_REPLY.BLOOD_PRESSURE,
    keywords: ['blood pressure', ' bp '],
    actions: [ASSISTANT_ACTION.OPEN_FLUID],
  },
  {
    reply: ASSISTANT_REPLY.BLOOD_TEST,
    keywords: ['blood test', 'lab result', 'lab ', 'hemoglobin', 'haemoglobin'],
    actions: [ASSISTANT_ACTION.OPEN_RESULTS],
  },
  {
    reply: ASSISTANT_REPLY.MEDICATION,
    keywords: ['medic', 'pill', 'tablet', 'dose'],
    actions: [ASSISTANT_ACTION.MEDICATION_REMINDERS],
  },
  {
    reply: ASSISTANT_REPLY.GOALS,
    keywords: ['goal'],
    actions: [ASSISTANT_ACTION.GOALS],
  },
  {
    reply: ASSISTANT_REPLY.SYMPTOM,
    keywords: ['symptom', 'dizz', 'swell', 'cramp', 'itch', 'tired', 'nausea', 'pain'],
    actions: [ASSISTANT_ACTION.OPEN_SYMPTOMS, ASSISTANT_ACTION.CALL_CARE_TEAM],
  },
  {
    reply: ASSISTANT_REPLY.SCHEDULE,
    keywords: ['schedule', 'dialysis day', 'attend', 'session'],
    actions: [ASSISTANT_ACTION.OPEN_PROFILE],
  },
  {
    reply: ASSISTANT_REPLY.LANGUAGE,
    keywords: ['language', '中文', 'chinese', 'english', 'tiếng'],
    actions: [ASSISTANT_ACTION.SWITCH_LANGUAGE],
  },
  {
    reply: ASSISTANT_REPLY.LOGIN,
    keywords: ['log in', 'login', 'password', 'can’t log', 'cant log'],
    actions: [ASSISTANT_ACTION.CALL_SUPPORT],
  },
  {
    reply: ASSISTANT_REPLY.CONTACT,
    keywords: ['call', 'contact', 'nurse', 'doctor', 'care team', 'phone'],
    actions: [ASSISTANT_ACTION.CALL_CARE_TEAM, ASSISTANT_ACTION.SEE_CONTACTS],
  },
  {
    reply: ASSISTANT_REPLY.APPROXIMATE,
    keywords: ['approximate', 'estimate', 'accurate', 'ai '],
    actions: [],
  },
  {
    reply: ASSISTANT_REPLY.WEEKLY,
    keywords: ['weekly', 'summary', 'report'],
    actions: [ASSISTANT_ACTION.WEEKLY_SUMMARY],
  },
]);

export const ASSISTANT_FALLBACK_RULE = Object.freeze({
  reply: ASSISTANT_REPLY.FALLBACK,
  actions: [ASSISTANT_ACTION.CALL_CARE_TEAM, ASSISTANT_ACTION.OPEN_FAQ],
});

const MESSAGE_PREFIX = 'nkf.assistant.suggestions.';

// Suggestion chips send their label as the question and answer with the named reply.
const SUGGESTION = Object.freeze({
  IDWG: { labelKey: MESSAGE_PREFIX + 'idwg', reply: ASSISTANT_REPLY.IDWG },
  DRY_WEIGHT_CHANGE: {
    labelKey: MESSAGE_PREFIX + 'dryWeightChange',
    reply: ASSISTANT_REPLY.DRY_WEIGHT,
  },
  CALL_CARE_TEAM: { labelKey: MESSAGE_PREFIX + 'callCareTeam', reply: ASSISTANT_REPLY.CONTACT },
  LOG_MEAL: { labelKey: MESSAGE_PREFIX + 'logMeal', reply: ASSISTANT_REPLY.MEAL },
  POTASSIUM_FOODS: {
    labelKey: MESSAGE_PREFIX + 'potassiumFoods',
    reply: ASSISTANT_REPLY.POTASSIUM,
  },
  WEEKLY_SUMMARY: { labelKey: MESSAGE_PREFIX + 'weeklySummary', reply: ASSISTANT_REPLY.WEEKLY },
  WHEN_CALL_CARE_TEAM: {
    labelKey: MESSAGE_PREFIX + 'whenCallCareTeam',
    reply: ASSISTANT_REPLY.CONTACT,
  },
  LOG_SYMPTOM: { labelKey: MESSAGE_PREFIX + 'logSymptom', reply: ASSISTANT_REPLY.SYMPTOM },
});

export const ASSISTANT_DEFAULT_SUGGESTIONS = Object.freeze([
  SUGGESTION.LOG_MEAL,
  SUGGESTION.IDWG,
  SUGGESTION.CALL_CARE_TEAM,
]);

export const ASSISTANT_TAB_SUGGESTIONS = Object.freeze({
  [APP_TAB.FLUID]: [SUGGESTION.IDWG, SUGGESTION.DRY_WEIGHT_CHANGE, SUGGESTION.CALL_CARE_TEAM],
  [APP_TAB.DIET]: [SUGGESTION.LOG_MEAL, SUGGESTION.POTASSIUM_FOODS, SUGGESTION.WEEKLY_SUMMARY],
  [APP_TAB.SYMPTOMS]: [SUGGESTION.WHEN_CALL_CARE_TEAM, SUGGESTION.LOG_SYMPTOM],
});
